import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Calendar,
  Users,
  MoreHorizontal,
  Wrench,
  UserCircle,
  FileText,
  Calculator,
  Package,
  BarChart3,
  Settings,
  X,
  RefreshCw,
  MapPin,
  CalendarPlus,
  Trophy,
  Star,
  LayoutGrid,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores/authStore';

// ──────────────────────────────────────────────
// MobileNav — Bottom navigation bar for mobile
// ──────────────────────────────────────────────

interface MobileNavItem {
  labelKey: string;
  href: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  permission?: string;
}

const allPrimaryItems: MobileNavItem[] = [
  { labelKey: 'nav.dashboard', href: '/', icon: LayoutDashboard, end: true },
  { labelKey: 'nav.workOrders', href: '/work-orders', icon: ClipboardList, permission: 'work_orders:view' },
  { labelKey: 'nav.myWork', href: '/my-work', icon: Wrench, permission: 'work_orders:view' },
  { labelKey: 'nav.calendar', href: '/calendar', icon: Calendar, permission: 'calendar:view' },
];

const allMoreItems: MobileNavItem[] = [
  { labelKey: 'nav.dispatch', href: '/dispatch', icon: LayoutGrid, permission: 'calendar:view' },
  { labelKey: 'nav.customers', href: '/customers', icon: Users, permission: 'customers:view' },
  { labelKey: 'nav.team', href: '/team', icon: UserCircle, permission: 'team:view' },
  { labelKey: 'nav.invoices', href: '/invoices', icon: FileText, permission: 'invoices:view' },
  { labelKey: 'nav.estimates', href: '/estimates', icon: Calculator, permission: 'estimates:view' },
  { labelKey: 'nav.contracts', href: '/contracts', icon: RefreshCw, permission: 'contracts:view' },
  { labelKey: 'nav.bookings', href: '/bookings', icon: CalendarPlus, permission: 'bookings:view' },
  { labelKey: 'nav.inventory', href: '/inventory', icon: Package, permission: 'inventory:view' },
  { labelKey: 'nav.tracking', href: '/tracking', icon: MapPin, permission: 'tracking:view' },
  { labelKey: 'nav.reports', href: '/reports', icon: BarChart3, permission: 'reports:view' },
  { labelKey: 'nav.csat', href: '/reports/csat', icon: Star, permission: 'csat:view' },
  { labelKey: 'nav.leaderboard', href: '/leaderboard', icon: Trophy, permission: 'gamification:view' },
  { labelKey: 'nav.settings', href: '/settings', icon: Settings, permission: 'settings:view' },
];

function MobileNav() {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const permissions = useAuthStore((s) => s.permissions);

  const filterByPermission = (items: MobileNavItem[]) =>
    items.filter((item) => {
      if (!item.permission) return true;
      return permissions.includes('*') || permissions.includes(item.permission);
    });

  const primaryItems = useMemo(() => filterByPermission(allPrimaryItems), [permissions]);
  const moreItems = useMemo(() => filterByPermission(allMoreItems), [permissions]);

  // Close sheet on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Close on escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setMoreOpen(false);
  }, []);

  useEffect(() => {
    if (moreOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [moreOpen, handleKeyDown]);

  // Check if any "more" item is active
  const isMoreActive = moreItems.some((item) =>
    item.end ? location.pathname === item.href : location.pathname.startsWith(item.href)
  );

  return (
    <>
      {/* Bottom nav bar */}
      <nav
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white',
          'pb-[env(safe-area-inset-bottom)] md:hidden',
          'dark:border-slate-800 dark:bg-slate-950'
        )}
      >
        <div className="flex h-16 items-center justify-around px-2">
          {primaryItems.map((item) => {
            const isActive = item.end
              ? location.pathname === item.href
              : location.pathname.startsWith(item.href);

            return (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.end}
                className="flex flex-1 flex-col items-center gap-0.5 py-1"
              >
                <item.icon
                  className={cn(
                    'h-5 w-5 transition-colors',
                    isActive ? 'text-indigo-600' : 'text-slate-400'
                  )}
                />
                <span
                  className={cn(
                    'text-[10px] font-medium transition-colors',
                    isActive ? 'text-indigo-600' : 'text-slate-500'
                  )}
                >
                  {t(item.labelKey)}
                </span>
              </NavLink>
            );
          })}

          {/* More button */}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex flex-1 flex-col items-center gap-0.5 py-1"
          >
            <MoreHorizontal
              className={cn(
                'h-5 w-5 transition-colors',
                isMoreActive || moreOpen ? 'text-indigo-600' : 'text-slate-400'
              )}
            />
            <span
              className={cn(
                'text-[10px] font-medium transition-colors',
                isMoreActive || moreOpen ? 'text-indigo-600' : 'text-slate-500'
              )}
            >
              {t('nav.more')}
            </span>
          </button>
        </div>
      </nav>

      {/* "More" sheet / drawer */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm md:hidden"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />

          {/* Sheet */}
          <div
            className={cn(
              'fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-white',
              'pb-[env(safe-area-inset-bottom)] md:hidden',
              'animate-in slide-in-from-bottom duration-200',
              'dark:bg-slate-950'
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Menu adicional"
          >
            {/* Drag handle */}
            <div className="flex justify-center py-3">
              <div className="h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-700" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-4 pb-3 dark:border-slate-800">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                Menu
              </h2>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="Cerrar menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Items grid */}
            <div className="grid grid-cols-3 gap-2 p-4">
              {moreItems.map((item) => {
                const isActive = item.end
                  ? location.pathname === item.href
                  : location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.end}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-xl p-3 transition-colors',
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                        : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50'
                    )}
                  >
                    <item.icon className={cn('h-6 w-6', isActive ? 'text-indigo-600' : 'text-slate-400')} />
                    <span className="text-xs font-medium">{t(item.labelKey)}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}

MobileNav.displayName = 'MobileNav';

export { MobileNav };
